// src/pages/OperationalPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const reportTypes = [
  'At Sea',
  'In Port',
  'Anchor',
  'Drifting',
  'Harbour Streaming',
  'Drydock',
  'Layup'
];

const OperationalPage = () => {
  const [editingId, setEditingId]       = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [fields, setFields] = useState([
    {
      section: '',
      reportType: '',
      fieldName: '',
      units: '',
      type: 'String',
      dropdownList: '',
      validationRule: '',
      mandatory: false
    }
  ]);
  const [message, setMessage]     = useState('');
  const [templates, setTemplates] = useState([]);
  const formRef                   = useRef(null);

  // Load all templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/operational`);
      if (!res.ok) throw new Error(res.statusText);
      setTemplates(await res.json());
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/operational/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setTemplates(tpls => tpls.filter(t => t._id !== id));
        if (editingId === id) resetForm();
      }
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const editTemplate = (tpl) => {
    setEditingId(tpl._id);
    setTemplateName(tpl.templateName);
    setFields(
      tpl.fields.map(f => ({
        section: f.section,
        reportType: f.reportType || '',
        fieldName: f.fieldName,
        units: f.units || '',
        type: f.type,
        dropdownList: f.dropdownList,
        validationRule: f.validationRule,
        mandatory: f.mandatory
      }))
    );
    formRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingId(null);
    setTemplateName('');
    setFields([
      {
        section: '',
        reportType: '',
        fieldName: '',
        units: '',
        type: 'String',
        dropdownList: '',
        validationRule: '',
        mandatory: false
      }
    ]);
    setMessage('');
  };

  const addField = () => {
    setFields(flds => [
      ...flds,
      {
        section: '',
        reportType: '',
        fieldName: '',
        units: '',
        type: 'String',
        dropdownList: '',
        validationRule: '',
        mandatory: false
      }
    ]);
  };

  const removeField = (i) =>
    setFields(flds => flds.filter((_, idx) => idx !== i));

  const handleFieldChange = (i, key, val) => {
    setFields(flds => {
      const copy = [...flds];
      copy[i] = { ...copy[i], [key]: val };
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { templateName, fields };

    try {
      let res;
      if (editingId) {
        res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/operational/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/operational`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessage(editingId ? 'Template updated successfully!' : 'Template created successfully!');
      resetForm();
      fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setMessage(`Failed to ${editingId ? 'update' : 'save'}: ${err.message}`);
    }
  };

  return (
    <div className="container-fluid px-4 py-4">
      <h2>Operational Template</h2>
      <p>Configure the fields for the Operational section of the Performance Report.</p>

      {/* Existing Templates */}
      <h4>Existing Templates</h4>
      {templates.length ? (
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th>Template Name</th>
              <th style={{ width: '10%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl, idx) => (
              <tr
                key={tpl._id}
                style={{ cursor: 'pointer' }}
                onClick={() => editTemplate(tpl)}
              >
                <td>{idx + 1}</td>
                <td>{tpl.templateName}</td>
                <td className="text-center">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={e => {
                      e.stopPropagation();
                      deleteTemplate(tpl._id);
                    }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No templates found.</p>
      )}

      {/* Form */}
      <div ref={formRef} />
      <h4 className="mt-4">{editingId ? 'Edit Template' : 'Create New Template'}</h4>
      <form onSubmit={handleSubmit}>
        {/* Template Name */}
        <div className="mb-3">
          <label className="form-label">Template Name</label>
          <input
            className="form-control form-control-sm"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            required
          />
        </div>

        {/* Fields */}
        <h5>Fields</h5>
        {fields.map((field, i) => (
          <div key={i} className="mb-2 border p-2">
            <div className="row g-2 align-items-center">
              {/* Section */}
              <div className="col-auto">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Section"
                  value={field.section}
                  onChange={e => handleFieldChange(i, 'section', e.target.value)}
                  required
                />
              </div>

              {/* Report Type */}
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  value={field.reportType}
                  onChange={e => handleFieldChange(i, 'reportType', e.target.value)}
                  required
                >
                  <option value="">Report Type</option>
                  {reportTypes.map(rt => (
                    <option key={rt} value={rt}>
                      {rt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Field Name */}
              <div className="col-auto">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Field Name"
                  value={field.fieldName}
                  onChange={e => handleFieldChange(i, 'fieldName', e.target.value)}
                  required
                />
              </div>

              {/* Units */}
              <div className="col-auto">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Units"
                  value={field.units}
                  onChange={e => handleFieldChange(i, 'units', e.target.value)}
                />
              </div>

              {/* Type of Entry */}
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  value={field.type}
                  onChange={e => handleFieldChange(i, 'type', e.target.value)}
                >
                  <option>String</option>
                  <option>Datetime</option>
                  <option>Number</option>
                  <option>Decimal</option>
                  <option>Dropdown</option>
                </select>
              </div>

              {/* Validation Rule */}
              <div className="col-auto">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Validation Rule"
                  value={field.validationRule}
                  onChange={e => handleFieldChange(i, 'validationRule', e.target.value)}
                />
              </div>

              {/* Mandatory */}
              <div className="col-auto">
                <div className="form-check form-check-inline">
                  <input
                    id={`mand-${i}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={field.mandatory}
                    onChange={e => handleFieldChange(i, 'mandatory', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`mand-${i}`}>
                    Mandatory
                  </label>
                </div>
              </div>

              {/* Remove Field */}
              <div className="col-auto">
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeField(i)}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Dropdown List input */}
            {field.type === 'Dropdown' && (
              <div className="row g-2 mt-1">
                <div className="col-auto">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Dropdown options, comma-separated"
                    value={field.dropdownList}
                    onChange={e => handleFieldChange(i, 'dropdownList', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Action Buttons */}
        <div className="d-flex gap-2 mb-3">
          <button type="button" className="btn btn-secondary btn-sm" onClick={addField}>
            Add New Field
          </button>
          <button type="submit" className="btn btn-primary btn-sm">
            {editingId ? 'Update Template' : 'Save Template'}
          </button>
          {editingId && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {message && <div className="alert alert-info mt-3">{message}</div>}
    </div>
  );
};

export default OperationalPage;
